<template>
  <div
      :class="[
      'evan-switch',
      vertical ? 'evan-switch--vertical' : 'evan-switch--horizontal',
      border && 'evan-switch--bordered'
    ]"
      :style="containerStyle"
      aria-label="切换选项"
      role="group"
  >
    <!-- 滑块指示器 -->
    <div
        :aria-hidden="true"
        :style="sliderStyle"
        class="evan-switch__slider"
    ></div>

    <!-- 选项列表 -->
    <div
        v-for="(item, index) in normalizedOptions"
        :key="generateKey(item, index)"
        :aria-label="getItemLabel(item)"
        :aria-selected="isActive(index)"
        :class="[
        'evan-switch__option',
        { 'evan-switch__option--active': isActive(index) },
        {'evan-switch__option--disabled':isDisabled(item)}
      ]"
        :style="optionStyle"
        role="radio"
        tabindex="0"
        @click="handleOptionClick(getItemValue(item), index)"
        @keydown.enter="handleOptionClick(getItemValue(item), index)"
        @keydown.space.prevent="handleOptionClick(getItemValue(item), index)"
    >
      {{ getItemLabel(item) }}
    </div>
  </div>
</template>

<script>

// 工具函数 - 深度比较两个值是否相等
const isEqual = (a, b) => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a === 'object' && a !== null && b !== null) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
};

export default {
  name: 'EvanSwitch',
  model: {
    prop: 'value',
    event: 'update:value',
  },
  props: {
    // 当前选中的值
    value: {
      default: null,
    },
    // 选项列表，可以是字符串数组或对象数组
    options: {
      type: Array,
      default: () => [],
      required: true,
      validator: (value) => {
        return Array.isArray(value) && value.length > 0;
      }
    },
    // 对象选项中标签的属性名
    labelKey: {
      type: String,
      default: 'label',
    },
    // 对象选项中值的属性名
    valueKey: {
      type: String,
      default: 'value'
    },
    // 输出格式：'value' | 'label' | 'object' | 'auto'
    outputType: {
      type: String,
      default: 'auto',
      validator: (value) => {
        return ['value', 'label', 'object', 'auto', 'index'].includes(value);
      }
    },
    // 是否为垂直布局
    vertical: {
      type: Boolean,
      default: false,
    },
    // 背景颜色
    backgroundColor: {
      type: String,
      default: 'var(--bg-color, #f5f5f5)'
    },
    // 激活状态颜色
    activeColor: {
      type: String,
      default: 'var(--active-color, #409eff)'
    },
    // 字体大小
    size: {
      type: String,
      default: '1em',
    },
    // 圆角大小
    borderRadius: {
      type: String,
      default: '5px',
    },
    // 是否显示边框
    border: {
      type: Boolean,
      default: false,
    },
    // 是否禁用组件
    disabled: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      currentIndex: 0, // 当前选中项的索引
    };
  },
  computed: {
    /**
     * 规范化选项数据，统一处理字符串和对象格式
     */
    normalizedOptions() {
      return this.options.map((item, index) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return {
            label: String(item),
            value: item,
            index
          };
        }
        return {
          label: item[this.labelKey] || '',
          value: item[this.valueKey] !== undefined ? item[this.valueKey] : item,
          ...item,
          index
        };
      });
    },

    /**
     * 容器样式
     */
    containerStyle() {
      const style = {
        fontSize: this.size,
        backgroundColor: this.backgroundColor,
        borderRadius: this.borderRadius,
        gridTemplateColumns: `repeat(${this.options.length}, 1fr)`,
        gridTemplateRows: `repeat(${this.options.length}, 1fr)`,
      };

      // 根据布局方向设置网格布局
      if (this.vertical) {
        style.gridTemplateColumns = '1fr';
        style.gridTemplateRows = `repeat(${this.options.length}, 1fr)`;
      } else {
        style.gridTemplateColumns = `repeat(${this.options.length}, 1fr)`;
        style.gridTemplateRows = '1fr';
      }

      // 边框样式
      if (this.border) {
        style.border = '.1em solid var(--border-color, #dcdfe6)';
      }

      return style;
    },

    /**
     * 滑块样式
     */
    sliderStyle() {
      const position = this.currentIndex * 100;
      const dimension = 100 / this.options.length;

      const baseStyle = {
        borderRadius: this.borderRadius,
        backgroundColor: this.activeColor,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // 更平滑的动画曲线
      };

      if (this.vertical) {
        return {
          ...baseStyle,
          width: this.border ? 'calc(100% - 0.2em)' : '100%',
          height: `calc(${this.border ? 'calc(100% - 0.2em)' : '100%'} / ${this.options.length})`,
          transform: `translateY(${position}%)`,
          left: this.border ? '0.1em' : '0',
          top: this.border ? '0.1em' : '0',
        };
      } else {
        return {
          ...baseStyle,
          height: this.border ? 'calc(100% - 0.2em)' : '100%',
          width: `calc(${this.border ? 'calc(100% - 0.2em)' : '100%'} / ${this.options.length})`,
          transform: `translateX(${position}%)`,
          left: this.border ? '0.1em' : '0',
          top: this.border ? '0.1em' : '0',
        };
      }
    },

    /**
     * 选项样式
     */
    optionStyle() {
      return {
        fontSize: 'inherit', // 继承容器字体大小
      };
    },
  },
  watch: {
    // 监听外部value变化，同步内部状态
    value: {
      handler(newVal) {
        this.syncCurrentIndex(newVal);
      },
      immediate: true,
    },
    // 监听选项变化，重新同步状态
    options: {
      handler() {
        this.$nextTick(() => {
          this.syncCurrentIndex(this.value);
        });
      },
      deep: true,
    },
  },
  methods: {
    /**
     * 生成唯一的key
     */
    generateKey(item, index) {
      if (typeof item === 'object' && item.id) {
        return `evan-switch-option-${item.id}`;
      }
      return `evan-switch-option-${index}-${JSON.stringify(item)}`;
    },

    /**
     * 获取选项的显示标签
     */
    getItemLabel(item) {
      return item[this.labelKey] || String(item);
    },

    /**
     * 获取选项的值
     */
    getItemValue(item) {
      if (typeof item === 'object') {
        return item.value !== undefined ? item.value : item;
      }
      return item;
    },

    /**
     * 根据输出类型格式化返回值
     */
    formatOutputValue(item, index) {
      switch (this.outputType) {
        case 'value':
          return this.getItemValue(item);
        case 'label':
          return this.getItemLabel(item);
        case 'object':
          return item;
        case 'index':
          return index;
        case 'auto':
        default:
          // 自动判断：优先返回value，其次index，最后返回整个item
          const value = this.getItemValue(item);
          return this.$CF.isEffective(value) ? value : (this.$CF.isEffective(index) ? index : item);
      }
    },

    /**
     * 判断选项是否激活
     * @param index 当前选项的index
     * @returns {boolean} true:当前选中元素，false:不是当前选中的
     */
    isActive(index) {
      return this.currentIndex === index;
    },
    /**
     * 判断选项是否可以选择
     * @param item 当前选项
     * @returns {boolean} true:可选择，false:不可选择
     */
    isDisabled(item) {
      return typeof item === 'object' && item.hasOwnProperty('disabled') && typeof item.disabled === 'boolean' && item.disabled;
    },
    /**
     * 处理选项点击事件
     */
    handleOptionClick(itemValue, index) {
      console.log("响应")
      if (this.disabled || this.isActive(index)) {
        return; // 禁用或点击当前选项时不做处理
      }

      const outputValue = this.formatOutputValue(this.normalizedOptions[index], index);

      // 更新内部状态
      this.currentIndex = index;

      // 触发事件
      this.$emit('click', outputValue, index);
      this.$emit('update:value', outputValue);
      this.$emit('change', {
        value: outputValue,
        index,
        item: this.normalizedOptions[index]
      });
    },

    /**
     * 根据外部value同步当前选中索引
     */
    syncCurrentIndex(value) {
      if (value === null || value === undefined) {
        this.currentIndex = 0;
        return;
      }

      // 查找匹配的选项
      const index = this.normalizedOptions.findIndex((item, idx) => {
        const itemValue = this.getItemValue(item);

        // 优先使用深度比较
        if (isEqual(itemValue, value)) {
          return true;
        }

        // 其次比较格式化后的值
        if (isEqual(this.formatOutputValue(item, idx), value)) {
          return true;
        }

        return false;
      });

      this.currentIndex = index >= 0 ? index : 0;
    },
  },
  mounted() {
    // 初始同步
    this.syncCurrentIndex(this.value);
  },
};
</script>

<style lang="scss" scoped>
.evan-switch {
  position: relative;
  display: grid;
  min-height: 1.5em;
  padding: 0.1em;
  box-sizing: border-box;
  font-size: 14px; // 默认字体大小

  // 禁用状态
  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;

    .evan-switch__option {
      cursor: not-allowed;
    }
  }

  // 滑块样式
  &__slider {
    position: absolute;
    z-index: 1;
    pointer-events: none; // 防止滑块拦截点击事件
  }

  // 选项基础样式
  &__option {
    position: relative;
    z-index: 1; // 确保选项在滑块上方
    cursor: pointer;
    user-select: none;
    text-align: center;
    padding: 0.4em 0.8em;
    line-height: 1.2;
    transition: color 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100%;
    box-sizing: border-box;

    // 激活状态
    &--active {
      color: #fff;
      font-weight: 500;
    }
    //禁用状态
    &--disabled {
      pointer-events: none;
      filter: grayscale(0.5) brightness(0.9);
      opacity: 0.7;
      cursor: not-allowed;
    }
    // 非激活状态
    &:not(.evan-switch__option--active) {
      color: var(--text-color, #606266);
    }

    // 悬停效果
    &:hover:not(.evan-switch__option--active) {
      color: var(--active-color, #409eff);
    }

    // 聚焦样式

  }

  // 水平布局
  &--horizontal {
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    grid-template-rows: 1fr;
  }

  // 垂直布局
  &--vertical {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(auto-fit, minmax(0, 1fr));
  }

  // 边框样式
  &--bordered {
    border: 1px solid var(--border-color, #dcdfe6);
  }
}
</style>
